import React, { useState, memo, useRef, useEffect } from 'react';
import { SparkModalPortal, Modal as SparkModal, Button, SparkIcon, TooltipTrigger, Toggle, Skeleton, sparkToast, ReadOnlyGuard } from '@spark';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { formatDateTime } from "@/shared/utils/helpers"
import { editRawRecord } from '@/features/datasets/components/DataSelection/RawRecordsStatus/service';
import { handleCopy } from '@/shared/utils/helpers';
// Custom scrollbar styles for dark theme
const scrollbarStyles = {
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1)',
};

// Modal content background
const modalBackground = {
    background: '#121212',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
};

// Copy button style
const actionButtonStyle = {
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.65)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    zIndex: 10,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
};

// Hover effect for button
const actionButtonHoverStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: 'rgba(255, 255, 255, 0.9)',
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
};

/**
 * Modal component for displaying raw record details
 */
const RawExampleModal = memo(({
    visible,
    onClose,
    loading,
    record,
    onApplyFilter = () => { },
    onRecordUpdated = () => { },
    clientId,
    usecaseId,
    setStatsData
}) => {
    // Copy button states
    const [promptCopied, setPromptCopied] = useState(false);
    const [responseCopied, setResponseCopied] = useState(false);
    const [parsedPromptCopied, setParsedPromptCopied] = useState(false);
    const [parsedResponseCopied, setParsedResponseCopied] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Edit mode states
    const [isEditingPrompt, setIsEditingPrompt] = useState(false);
    const [isEditingResponse, setIsEditingResponse] = useState(false);
    const [editedPrompt, setEditedPrompt] = useState("");
    const [editedResponse, setEditedResponse] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [processingStatus, setProcessingStatus] = useState(null);

    // Confirm save dialog state (replaces Popconfirm)
    const [confirmSaveTarget, setConfirmSaveTarget] = useState(null);

    // Container refs for position calculations
    const promptContainerRef = useRef(null);
    const responseContainerRef = useRef(null);
    const parsedPromptContainerRef = useRef(null);
    const parsedResponseContainerRef = useRef(null);

    // Set initial values when record changes
    useEffect(() => {
        if (record) {
            setEditedPrompt(record.raw_prompt || "");
            setEditedResponse(record.raw_response || "");
        }
    }, [record]);

    // Reset edit state when modal is closed
    useEffect(() => {
        if (!visible) {
            setIsEditingPrompt(false);
            setIsEditingResponse(false);
            setProcessingStatus(null);
            setConfirmSaveTarget(null);
        }
    }, [visible]);

    // Handle saving edited prompt
    const handleSavePrompt = async () => {
        if (!record) return;

        try {
            setIsSaving(true);
            setProcessingStatus("Processing prompt and extracting example...");

            const result = await editRawRecord({
                clientId,
                usecaseId,
                recordId: record.id,
                rawPrompt: editedPrompt,
            });

            if (result.example_success) {
                sparkToast.success("Prompt updated and example extracted successfully");
            } else {
                sparkToast.success("Prompt updated successfully");
                sparkToast.warning("Failed to extract example from updated prompt");
            }

            setIsEditingPrompt(false);
            onRecordUpdated(result);
        } catch (error) {
            sparkToast.error(`Failed to update prompt: ${error.message}`);
        } finally {
            setIsSaving(false);
            setProcessingStatus(null);
        }
    };

    // Handle saving edited response
    const handleSaveResponse = async () => {
        if (!record) return;

        try {
            setIsSaving(true);
            setProcessingStatus("Processing response and extracting structured data...");

            const result = await editRawRecord({
                clientId,
                usecaseId,
                recordId: record.id,
                rawResponse: editedResponse,
            });

            if (result.response_success) {
                sparkToast.success("Response updated and structured data extracted successfully");
            } else {
                sparkToast.success("Response updated successfully");
                sparkToast.warning("Failed to extract structured data from updated response");
            }

            setIsEditingResponse(false);
            onRecordUpdated(result);

            if (result.statsData) {
                setStatsData(result.statsData);
            }
        } catch (error) {
            sparkToast.error(`Failed to update response: ${error.message}`);
        } finally {
            setIsSaving(false);
            setProcessingStatus(null);
        }
    };

    // Copy function with visual feedback
    const copyToClipboard = (text, setCopiedState) => {
        if (!text) return;

        handleCopy(text)
            .then(() => {
                setCopiedState(true);
                sparkToast.success('Copied to clipboard!');
                setTimeout(() => {
                    setCopiedState(false);
                }, 2000);
            })
            .catch(() => {
                sparkToast.error('Failed to copy');
            });
    };

    // Function to extract a search term from text
    const extractSearchTerm = (text, maxLength = 20) => {
        if (!text) return '';
        const words = text.split(/\s+/);
        let result = '';
        for (const word of words) {
            if ((result + ' ' + word).length <= maxLength) {
                result += (result ? ' ' : '') + word;
            } else {
                break;
            }
        }
        return result.trim();
    };

    // Handler for filtering by response content
    const handleFilterByResponse = () => {
        if (!record?.raw_response) return;
        const searchTerm = extractSearchTerm(record.raw_response);
        onApplyFilter('rawResponseQuery', searchTerm);
        onClose();
    };

    // Toggle fullscreen mode
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    // Function to render the parsed example section when available
    const renderParsedSections = () => {
        if (!record) return null;

        const hasExampleData = record.example_success !== null;
        const hasResponseData = record.response_success !== null;

        if (!hasExampleData && !hasResponseData) return null;

        const animationStyle = `
            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
            }
            @keyframes errorPulse {
                0% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.4); }
                70% { box-shadow: 0 0 0 6px rgba(255, 77, 79, 0); }
                100% { box-shadow: 0 0 0 0 rgba(255, 77, 79, 0); }
            }
            @keyframes fadeInWithBounce {
                0% { opacity: 0; transform: translateY(10px); }
                70% { opacity: 0.7; transform: translateY(-3px); }
                100% { opacity: 1; transform: translateY(0); }
            }
        `;

        return (
            <div style={{ marginTop: 40, animation: 'fadeIn 0.5s ease-in-out' }}>
                <style>{animationStyle}</style>
                <div style={{ position: 'relative' }}>
                    <div style={{
                        position: 'absolute',
                        top: -32,
                        left: 0,
                        width: '100%',
                        height: 2,
                        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                    }}></div>

                    <h4 style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: 24,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '16px',
                        fontWeight: 600,
                    }}>
                        {record.example_success === true && record.response_success === true ? (
                            <SparkIcon slug="check-circle" size="small" />
                        ) : (record.example_success === true || record.response_success === true) ? (
                            <span style={{ color: '#faad14' }}>⚠</span>
                        ) : (
                            <span style={{ color: '#ff4d4f' }}>✗</span>
                        )}
                        Parsed Data
                        <span style={{
                            fontSize: '14px',
                            fontWeight: 'normal',
                            color: 'rgba(255, 255, 255, 0.5)',
                            marginLeft: '8px'
                        }}>
                            {record.example_success === true && record.response_success === true
                                ? 'Successfully extracted structured data'
                                : (record.example_success === true || record.response_success === true)
                                    ? 'Partial success - some data extracted successfully'
                                    : 'Extraction results'}
                        </span>
                    </h4>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: hasExampleData && hasResponseData ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 24 }}>
                    {/* Render Example column */}
                    {hasExampleData && (
                        <div style={{
                            background: record.example_success
                                ? 'rgba(99, 102, 241, 0.08)'
                                : 'rgba(255, 77, 79, 0.08)',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            padding: '16px',
                            animation: 'fadeIn 0.6s ease-in-out',
                        }}>
                            <div style={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                marginBottom: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                paddingBottom: '12px',
                                fontWeight: 600,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{
                                        backgroundColor: record.example_success ? '#6366f1' : '#ff4d4f',
                                        width: 6,
                                        height: 20,
                                        display: 'inline-block',
                                        marginRight: 8,
                                        borderRadius: 3
                                    }}></span>
                                    Parsed Example
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        backgroundColor: record.example_success ? '#52c41a' : '#ff4d4f',
                                        display: 'inline-block',
                                    }} />
                                    <span style={{ color: record.example_success ? '#52c41a' : '#ff4d4f', fontSize: 13 }}>
                                        {record.example_success ? 'Extraction Successful' : 'Extraction Failed'}
                                    </span>
                                </div>
                            </div>

                            {record.example_success ? (
                                renderContentByType(record.request_example, parsedPromptCopied, setParsedPromptCopied, parsedPromptContainerRef, false, false, true)
                            ) : (
                                <div style={{
                                    padding: '16px',
                                    background: 'rgba(255, 77, 79, 0.05)',
                                    borderRadius: '6px',
                                    border: '1px dashed rgba(255, 77, 79, 0.3)',
                                    animation: 'errorPulse 2s infinite',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <span style={{ color: '#ff4d4f', fontSize: '16px', marginRight: '8px', marginTop: '3px' }}>⚠</span>
                                        <div>
                                            <strong style={{ color: '#ff4d4f', display: 'block', marginBottom: '4px' }}>
                                                Example Extraction Failed
                                            </strong>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '14px' }}>
                                                {record.example_failure_reason || "Failed to extract example from raw prompt."}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{
                                        marginTop: '16px',
                                        padding: '12px',
                                        background: 'rgba(0, 0, 0, 0.2)',
                                        borderRadius: '4px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                            <SparkIcon slug="info" size="small" />
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', fontWeight: 'bold', marginLeft: 6 }}>
                                                Troubleshooting Tips
                                            </span>
                                        </div>
                                        <ul style={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            fontSize: '13px',
                                            marginLeft: '8px',
                                            paddingLeft: '12px'
                                        }}>
                                            <li style={{ marginBottom: '4px' }}>Check the raw prompt format and structure</li>
                                            <li style={{ marginBottom: '4px' }}>Verify the prompt follows the expected Prompt Template configuration on the Plaground page</li>
                                        </ul>
                                    </div>

                                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                        <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(255, 77, 79, 0.2)', color: '#ff4d4f', fontSize: 12 }}>Extraction Failed</span>
                                        <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(250, 173, 20, 0.2)', color: '#faad14', fontSize: 12 }}>
                                            Error Code: {record.example_failure_reason ? 'PARSE_ERROR' : 'UNKNOWN_ERROR'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Render Response column */}
                    {hasResponseData && (
                        <div style={{
                            background: record.response_success
                                ? 'rgba(16, 185, 129, 0.08)'
                                : 'rgba(255, 77, 79, 0.08)',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            padding: '16px',
                            animation: 'fadeIn 0.8s ease-in-out',
                        }}>
                            <div style={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                marginBottom: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                paddingBottom: '12px',
                                fontWeight: 600,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{
                                        backgroundColor: record.response_success ? '#10b981' : '#ff4d4f',
                                        width: 6,
                                        height: 20,
                                        display: 'inline-block',
                                        marginRight: 8,
                                        borderRadius: 3
                                    }}></span>
                                    Parsed Response
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        backgroundColor: record.response_success ? '#52c41a' : '#ff4d4f',
                                        display: 'inline-block',
                                    }} />
                                    <span style={{ color: record.response_success ? '#52c41a' : '#ff4d4f', fontSize: 13 }}>
                                        {record.response_success ? 'Extraction Successful' : 'Extraction Failed'}
                                    </span>
                                </div>
                            </div>

                            {record.response_success ? (
                                renderContentByType(record.response, parsedResponseCopied, setParsedResponseCopied, parsedResponseContainerRef, false, false, true)
                            ) : (
                                <div style={{
                                    padding: '16px',
                                    background: 'rgba(255, 77, 79, 0.05)',
                                    borderRadius: '6px',
                                    border: '1px dashed rgba(255, 77, 79, 0.3)',
                                    animation: 'errorPulse 2s infinite',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        <span style={{ color: '#ff4d4f', fontSize: '16px', marginRight: '8px', marginTop: '3px' }}>⚠</span>
                                        <div>
                                            <strong style={{ color: '#ff4d4f', display: 'block', marginBottom: '4px' }}>
                                                Response Extraction Failed
                                            </strong>
                                            <span style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '14px' }}>
                                                {record.response_failure_reason || "Failed to extract structured data from raw response."}
                                            </span>
                                        </div>
                                    </div>

                                    <div style={{
                                        marginTop: '16px',
                                        padding: '12px',
                                        background: 'rgba(0, 0, 0, 0.2)',
                                        borderRadius: '4px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                            <SparkIcon slug="info" size="small" />
                                            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px', fontWeight: 'bold', marginLeft: 6 }}>
                                                Troubleshooting Tips
                                            </span>
                                        </div>
                                        <ul style={{
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            fontSize: '13px',
                                            marginLeft: '8px',
                                            paddingLeft: '12px'
                                        }}>
                                            <li style={{ marginBottom: '4px' }}>Check the raw response format and structure</li>
                                            <li style={{ marginBottom: '4px' }}>Verify the response follows the expected schema configured on the 'Response Parser' page</li>
                                        </ul>
                                    </div>

                                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                        <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(255, 77, 79, 0.2)', color: '#ff4d4f', fontSize: 12 }}>Extraction Failed</span>
                                        <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(250, 173, 20, 0.2)', color: '#faad14', fontSize: 12 }}>
                                            Error Code: {record.response_failure_reason ? 'PARSE_ERROR' : 'UNKNOWN_ERROR'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Custom button component
    const ActionButton = ({ onClick, copied, icon, tooltipText }) => {
        const [hover, setHover] = useState(false);

        return (
            <TooltipTrigger text={tooltipText} placement="top">
                <button
                    onClick={onClick}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                    style={{
                        ...actionButtonStyle,
                        ...(hover ? actionButtonHoverStyle : {}),
                        ...(copied ? { backgroundColor: '#52c41a', color: 'white' } : {})
                    }}
                    aria-label={tooltipText}
                >
                    {icon}
                </button>
            </TooltipTrigger>
        );
    };

    // Rendering edit controls for the prompt or response
    const renderEditControls = (isPrompt) => {
        const isEditing = isPrompt ? isEditingPrompt : isEditingResponse;
        const setIsEditing = isPrompt ? setIsEditingPrompt : setIsEditingResponse;
        const setEditedValue = isPrompt ? setEditedPrompt : setEditedResponse;
        const handleSave = isPrompt ? handleSavePrompt : handleSaveResponse;
        const originalValue = isPrompt ? record?.raw_prompt : record?.raw_response;

        if (isEditing) {
            return (
                <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '8px', zIndex: 11 }}>
                    <TooltipTrigger text="Discard changes" placement="top">
                        <Button
                            variant="secondary"
                            size="tiny"
                            startIcon={<SparkIcon slug="undo" size="small" />}
                            onClick={() => {
                                setEditedValue(originalValue || "");
                                setIsEditing(false);
                            }}
                        />
                    </TooltipTrigger>
                    <Button
                        variant="primary"
                        size="tiny"
                        startIcon={<SparkIcon slug="check" size="small" />}
                        onClick={() => setConfirmSaveTarget(isPrompt ? 'prompt' : 'response')}
                        disabled={isSaving}
                        disableWhenReadOnly
                    />
                </div>
            );
        }

        return (
            <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '8px', zIndex: 11 }}>
                {!isPrompt && (
                    <ActionButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleFilterByResponse();
                        }}
                        icon={<SparkIcon slug="filter" size="small" />}
                        tooltipText="Filter by similar responses"
                    />
                )}

                <ActionButton
                    onClick={(e) => {
                        e.stopPropagation();
                        const content = isPrompt ? record?.raw_prompt : record?.raw_response;
                        copyToClipboard(content, isPrompt ? setPromptCopied : setResponseCopied);
                    }}
                    copied={isPrompt ? promptCopied : responseCopied}
                    icon={<SparkIcon slug={isPrompt ? (promptCopied ? 'check' : 'copy') : (responseCopied ? 'check' : 'copy')} size="small" />}
                    tooltipText="Copy to clipboard"
                />

                <ReadOnlyGuard>
                    <ActionButton
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditing(true);
                        }}
                        icon={<SparkIcon slug="edit" size="small" />}
                        tooltipText={`Edit ${isPrompt ? 'prompt' : 'response'}`}
                    />
                </ReadOnlyGuard>
            </div>
        );
    };

    // Function to render JSON or text content
    const renderContentByType = (content, copied, setCopied, containerRef, isPrompt = false, isResponse = false, isParsedContent = false) => {
        if (!content) return <span style={{ color: 'rgba(255, 255, 255, 0.45)' }}>No content available</span>;

        // Check if we're in edit mode
        if (!isParsedContent && ((isPrompt && isEditingPrompt) || (isResponse && isEditingResponse))) {
            const editValue = isPrompt ? editedPrompt : editedResponse;
            const setEditValue = isPrompt ? setEditedPrompt : setEditedResponse;

            return (
                <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
                    {renderEditControls(isPrompt)}
                    <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                            width: '100%',
                            height: isFullScreen ? '70vh' : '450px',
                            background: '#1e1e1e',
                            color: 'rgba(255, 255, 255, 0.85)',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            resize: 'none',
                            border: '1px solid #434343',
                            borderRadius: '8px',
                            padding: '12px',
                            outline: 'none',
                            ...scrollbarStyles,
                        }}
                        placeholder={`Enter ${isPrompt ? 'prompt' : 'response'} text here...`}
                    />
                </div>
            );
        }

        // Common style for both JSON and text content
        const codeBlockStyle = {
            borderRadius: '8px',
            height: '100%',
            minHeight: '300px',
            maxHeight: isFullScreen ? '70vh' : '450px',
            overflow: 'auto',
            background: 'rgba(20, 20, 30, 0.95)',
            fontSize: '14px',
            ...scrollbarStyles,
        };

        // Line number styles
        const lineNumberStyle = {
            minWidth: '3.5em',
            paddingRight: '1em',
            background: 'rgba(15, 15, 25, 0.95)',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.3)',
            textAlign: 'right',
            userSelect: 'none',
        };

        // Add rendering for edit controls
        const renderActionButtons = () => {
            if (!isParsedContent && (isPrompt || isResponse)) {
                return renderEditControls(isPrompt);
            }

            return (
                <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '8px', zIndex: 11 }}>
                    <ActionButton
                        onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(typeof content === 'object' ? JSON.stringify(content, null, 2) : content, setCopied);
                        }}
                        copied={copied}
                        icon={<SparkIcon slug={copied ? 'check' : 'copy'} size="small" />}
                        tooltipText="Copy to clipboard"
                    />
                </div>
            );
        };

        // Function to nicely format classification or NER responses
        const formatLMResponse = (response) => {
            if (!response) return null;

            const getRandomPastelColor = (seed) => {
                const seedValue = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                const hue = (seedValue % 360);
                return `hsl(${hue}, 70%, 65%)`;
            };

            // Handle classification response
            if (response.labels && Array.isArray(response.labels)) {
                return (
                    <div style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.85)' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#10b981', fontSize: '16px' }}>Classification Response</strong>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {response.labels.map((label, index) => (
                                <span
                                    key={index}
                                    style={{
                                        backgroundColor: getRandomPastelColor(label),
                                        color: '#111',
                                        borderRadius: '12px',
                                        padding: '2px 10px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                        {response.extra_params && Object.keys(response.extra_params).length > 0 && (
                            <div style={{ marginTop: '12px', padding: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}>
                                <strong style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', display: 'block', marginBottom: '6px' }}>
                                    Additional Parameters:
                                </strong>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                                    {Object.entries(response.extra_params).map(([key, value]) => (
                                        <div key={key} style={{ marginBottom: '4px' }}>
                                            <strong style={{ color: '#10b981' }}>{key}:</strong> {JSON.stringify(value)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {response.is_validated && (
                            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#52c41a', display: 'inline-block' }} />
                                <span style={{ color: '#52c41a' }}>Validated</span>
                            </div>
                        )}
                    </div>
                );
            }

            // Handle NER response
            if (response.entity && Array.isArray(response.entity) &&
                response.entity_type && Array.isArray(response.entity_type)) {

                const entityTypes = [...new Set(response.entity_type)];
                const entityTypeColors = {};
                entityTypes.forEach(type => {
                    entityTypeColors[type] = getRandomPastelColor(type);
                });

                return (
                    <div style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.85)' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#10b981', fontSize: '16px' }}>Named Entity Recognition Response</strong>
                        </div>

                        {/* Legend for entity types */}
                        <div style={{
                            marginBottom: '16px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            padding: '8px',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '6px'
                        }}>
                            {entityTypes.map(type => (
                                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <div style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '3px',
                                        backgroundColor: entityTypeColors[type]
                                    }}></div>
                                    <span style={{ fontSize: '13px' }}>{type}</span>
                                </div>
                            ))}
                        </div>

                        <div>
                            {response.entity.map((entity, index) => (
                                <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                                    <span style={{
                                        backgroundColor: entityTypeColors[response.entity_type[index]] || '#6366f1',
                                        color: '#111',
                                        borderRadius: '12px',
                                        padding: '0 10px',
                                        marginRight: '8px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}>
                                        {response.entity_type[index]}
                                    </span>
                                    <span style={{
                                        padding: '4px 8px',
                                        background: 'rgba(255,255,255,0.08)',
                                        borderRadius: '4px'
                                    }}>{entity}</span>

                                    {response.start_index && response.start_index[index] !== undefined && (
                                        <span style={{
                                            marginLeft: '8px',
                                            fontSize: '12px',
                                            color: 'rgba(255,255,255,0.5)'
                                        }}>
                                            Position: {response.start_index[index]}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>

                        {response.extra_params && Object.keys(response.extra_params).length > 0 && (
                            <div style={{ marginTop: '12px', padding: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}>
                                <strong style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', display: 'block', marginBottom: '6px' }}>
                                    Additional Parameters:
                                </strong>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                                    {Object.entries(response.extra_params).map(([key, value]) => (
                                        <div key={key} style={{ marginBottom: '4px' }}>
                                            <strong style={{ color: '#10b981' }}>{key}:</strong> {JSON.stringify(value)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {response.is_validated && (
                            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#52c41a', display: 'inline-block' }} />
                                <span style={{ color: '#52c41a' }}>Validated</span>
                            </div>
                        )}
                    </div>
                );
            }

            // Handle response as string (GenerationResponse)
            if (response.response && typeof response.response === 'string') {
                return (
                    <div style={{ padding: '12px', color: 'rgba(255, 255, 255, 0.85)' }}>
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#10b981', fontSize: '16px' }}>Generation Response</strong>
                        </div>
                        <div style={{
                            padding: '12px',
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '6px',
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            maxHeight: '300px',
                            overflow: 'auto',
                            ...scrollbarStyles
                        }}>
                            {response.response}
                        </div>

                        {response.extra_params && Object.keys(response.extra_params).length > 0 && (
                            <div style={{ marginTop: '12px', padding: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}>
                                <strong style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', display: 'block', marginBottom: '6px' }}>
                                    Additional Parameters:
                                </strong>
                                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
                                    {Object.entries(response.extra_params).map(([key, value]) => (
                                        <div key={key} style={{ marginBottom: '4px' }}>
                                            <strong style={{ color: '#10b981' }}>{key}:</strong> {JSON.stringify(value)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {response.is_validated && (
                            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#52c41a', display: 'inline-block' }} />
                                <span style={{ color: '#52c41a' }}>Validated</span>
                            </div>
                        )}
                    </div>
                );
            }

            // If it's a string and appears to be valid JSON
            if (typeof response === 'string' && response.trim().startsWith('{')) {
                try {
                    const parsedResponse = JSON.parse(response);
                    return (
                        <SyntaxHighlighter
                            language="json"
                            style={dracula}
                            customStyle={codeBlockStyle}
                            showLineNumbers
                            lineNumberStyle={lineNumberStyle}
                            wrapLines={true}
                        >
                            {JSON.stringify(parsedResponse, null, 2)}
                        </SyntaxHighlighter>
                    );
                } catch (e) {
                    // Not valid JSON, fall through
                }
            }

            return null;
        };

        try {
            // Special case for LMResponse objects
            if (typeof content === 'object' && (content.labels || content.entity || content.response)) {
                const formattedResponse = formatLMResponse(content);
                if (formattedResponse) {
                    return (
                        <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
                            {renderActionButtons()}
                            <div style={{
                                ...codeBlockStyle,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                            }}>
                                {formattedResponse}
                            </div>
                        </div>
                    );
                }
            }

            // Fallback for unknown response types
            if (typeof content === 'object' && !Array.isArray(content)) {
                if (content.is_validated !== undefined) {
                    return (
                        <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
                            {renderActionButtons()}
                            <div style={{
                                ...codeBlockStyle,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                padding: '12px',
                            }}>
                                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <strong style={{ color: '#10b981', fontSize: '16px' }}>Parsed Response</strong>
                                    {content.is_validated && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#52c41a', display: 'inline-block' }} />
                                            <span style={{ color: '#52c41a' }}>Validated</span>
                                        </span>
                                    )}
                                </div>
                                <SyntaxHighlighter
                                    language="json"
                                    style={dracula}
                                    customStyle={{
                                        background: 'rgba(20, 20, 30, 0.5)',
                                        borderRadius: '6px',
                                        padding: '12px',
                                    }}
                                    showLineNumbers
                                    lineNumberStyle={lineNumberStyle}
                                    wrapLines={true}
                                >
                                    {JSON.stringify(content, null, 2)}
                                </SyntaxHighlighter>
                            </div>
                        </div>
                    );
                }
            }

            // Try to parse as JSON for better display
            const parsed = typeof content === 'object' ? content : JSON.parse(content);
            const jsonString = JSON.stringify(parsed, null, 2);

            return (
                <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
                    {renderActionButtons()}
                    <SyntaxHighlighter
                        language="json"
                        style={dracula}
                        customStyle={codeBlockStyle}
                        showLineNumbers
                        lineNumberStyle={lineNumberStyle}
                        wrapLines={true}
                    >
                        {jsonString}
                    </SyntaxHighlighter>
                </div>
            );
        } catch (e) {
            // Not valid JSON, display as plain text
            return (
                <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
                    {renderActionButtons()}
                    <SyntaxHighlighter
                        language="text"
                        style={dracula}
                        customStyle={codeBlockStyle}
                        showLineNumbers
                        lineNumberStyle={lineNumberStyle}
                        wrapLines={true}
                    >
                        {typeof content === 'string' ? content : String(content)}
                    </SyntaxHighlighter>
                </div>
            );
        }
    };

    // Render nothing if not visible
    if (!visible) return null;

    if (loading) {
        return (
            <SparkModalPortal open={visible} onClose={onClose} size="wide">
                <SparkModal
                    title="Raw Example Record"
                    onCloseClick={onClose}
                    showFooter={false}
                >
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Skeleton shapeType="circle" width="40px" height="40px" />
                        <div style={{ marginTop: 16, color: 'rgba(255, 255, 255, 0.85)' }}>Loading raw example data...</div>
                    </div>
                </SparkModal>
            </SparkModalPortal>
        );
    }

    return (
        <>
            <SparkModalPortal
                open={visible}
                onClose={onClose}
                size="wide"
                style={{ maxWidth: isFullScreen ? '90%' : '1100px' }}
            >
                <div style={{
                    backgroundColor: '#181818',
                    borderRadius: '12px',
                    width: isFullScreen ? '90vw' : '1100px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    ...scrollbarStyles,
                }}>
                    {/* Header */}
                    <div style={{
                        backgroundColor: '#202020',
                        borderRadius: '12px 12px 0 0',
                        padding: '16px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <SparkIcon slug="code" size="small" />
                            <span style={{ color: 'white', fontWeight: 600 }}>Raw Example Record</span>
                            {record?.id && <span style={{ color: 'rgba(255, 255, 255, 0.65)', marginLeft: 8 }}>ID: {record.id}</span>}
                        </div>
                        <button
                            onClick={onClose}
                            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18 }}
                            aria-label="Close"
                        >
                            ×
                        </button>
                    </div>

                    {/* Body */}
                    <div style={{
                        backgroundColor: '#181818',
                        color: 'white',
                        padding: isFullScreen ? '20px 30px' : '16px 24px',
                    }}>
                        {record ? (
                            <div style={modalBackground}>
                                <div style={{ marginBottom: 24 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                                        <div>
                                            <strong style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Created:</strong>{' '}
                                            <span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>{formatDateTime(record.timestamp)}</span>
                                        </div>
                                        <div>
                                            <strong style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Example Extraction:</strong>{' '}
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{
                                                    width: 6, height: 6, borderRadius: '50%',
                                                    backgroundColor: record.example_success ? '#52c41a' : (record.example_success === false ? '#ff4d4f' : '#faad14'),
                                                    display: 'inline-block',
                                                }} />
                                                <span style={{ color: record.example_success ? '#52c41a' : (record.example_success === false ? '#ff4d4f' : '#faad14') }}>
                                                    {record.example_success ? 'Successful' : (record.example_success === false ? 'Failed' : 'Not Processed')}
                                                </span>
                                            </span>
                                        </div>
                                        <div>
                                            <strong style={{ color: 'rgba(255, 255, 255, 0.85)' }}>Response Extraction:</strong>{' '}
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{
                                                    width: 6, height: 6, borderRadius: '50%',
                                                    backgroundColor: record.response_success ? '#52c41a' : (record.response_success === false ? '#ff4d4f' : '#faad14'),
                                                    display: 'inline-block',
                                                }} />
                                                <span style={{ color: record.response_success ? '#52c41a' : (record.response_success === false ? '#ff4d4f' : '#faad14') }}>
                                                    {record.response_success ? 'Successful' : (record.response_success === false ? 'Failed' : 'Not Processed')}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                                    <div>
                                        <h5 style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: 16, display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 600 }}>
                                            <span style={{
                                                backgroundColor: '#6366f1',
                                                width: 6,
                                                height: 20,
                                                display: 'inline-block',
                                                marginRight: 8,
                                                borderRadius: 3
                                            }}></span>
                                            Raw Prompt
                                        </h5>
                                        {renderContentByType(record.raw_prompt, promptCopied, setPromptCopied, promptContainerRef, true, false, false)}
                                    </div>
                                    <div>
                                        <h5 style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: 16, display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 600 }}>
                                            <span style={{
                                                backgroundColor: '#10b981',
                                                width: 6,
                                                height: 20,
                                                display: 'inline-block',
                                                marginRight: 8,
                                                borderRadius: 3
                                            }}></span>
                                            Raw Response
                                        </h5>
                                        {renderContentByType(record.raw_response, responseCopied, setResponseCopied, responseContainerRef, false, true, false)}
                                    </div>
                                </div>

                                {renderParsedSections()}
                            </div>
                        ) : (
                            <span style={{ color: 'rgba(255, 255, 255, 0.65)' }}>Record data not available</span>
                        )}
                    </div>

                    {/* Footer */}
                    <div style={{
                        backgroundColor: '#202020',
                        borderRadius: '0 0 12px 12px',
                        padding: '12px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 8,
                    }}>
                        {processingStatus && (
                            <div style={{ display: 'flex', alignItems: 'center', color: '#1890ff', marginRight: 'auto', gap: 6 }}>
                                <SparkIcon slug="refresh" size="small" />
                                <span>{processingStatus}</span>
                            </div>
                        )}
                        <Button
                            variant="ghost"
                            size="small"
                            onClick={toggleFullScreen}
                            startIcon={<SparkIcon slug={isFullScreen ? 'collapse' : 'expand'} size="small" />}
                        >
                            {isFullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
                        </Button>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </SparkModalPortal>

            {/* Confirm Save Dialog (replaces Popconfirm) */}
            <SparkModalPortal open={!!confirmSaveTarget} onClose={() => setConfirmSaveTarget(null)}>
                <SparkModal
                    title="Save changes?"
                    subtitle="This will also re-attempt extraction"
                    primaryLabel="Yes"
                    primaryDisableWhenReadOnly
                    tertiaryLabel="No"
                    onPrimaryClick={() => {
                        const handler = confirmSaveTarget === 'prompt' ? handleSavePrompt : handleSaveResponse;
                        handler();
                        setConfirmSaveTarget(null);
                    }}
                    onTertiaryClick={() => setConfirmSaveTarget(null)}
                    onCloseClick={() => setConfirmSaveTarget(null)}
                />
            </SparkModalPortal>
        </>
    );
});

export default RawExampleModal;
